import {
  FileSearch,
  FileSpreadsheet,
  BellRing,
  Mail,
  Globe,
  Users,
  FileSignature,
  Receipt,
  LifeBuoy,
  Landmark,
  ShieldCheck,
  ListChecks,
  ShoppingCart,
  ScanLine,
  BarChart3,
  type LucideIcon,
} from "lucide-react";

/**
 * Starter workflow templates for the gallery. Each template is a graph
 * SKELETON: nodes reference a `factoryKey` (a key in nodeFactory.createNodeByType)
 * so the editor builds them with valid default config, then the user fills in
 * the specifics and saves. `id`s are stable so edges connect deterministically.
 */
export interface TemplateNode {
  id: string;
  factoryKey: string;        // key understood by createNodeByType()
  label: string;             // shown as a step chip on the card
  position: { x: number; y: number };
}

export interface TemplateEdge {
  source: string;
  target: string;
  sourceHandle?: string;
}

export interface WorkflowTemplate {
  key: string;
  title: string;
  description: string;          // rich copy for the gallery card
  summary?: string;             // short subtitle loaded into the editor header
  category: string;
  icon: LucideIcon;
  nodes: TemplateNode[];
  edges: TemplateEdge[];
}

// Single left→right row layout. STEP must exceed the node card width
// (cards are ~320–420px wide) plus a gap, otherwise nodes overlap.
const X0 = 120;
const STEP = 500;
const Y = 160;
const at = (i: number) => ({ x: X0 + i * STEP, y: Y });

export const WORKFLOW_TEMPLATES: WorkflowTemplate[] = [
  {
    key: "doc-ocr-extract-store",
    title: "Document OCR → Extract → Store",
    description: "OCR an uploaded file, pull structured fields, and save it back to a document store.",
    category: "Documents",
    icon: FileSearch,
    nodes: [
      { id: "upload-1", factoryKey: "document-upload", label: "File Upload", position: at(0) },
      { id: "ocr-1", factoryKey: "ocr-processing", label: "OCR", position: at(1) },
      { id: "extract-1", factoryKey: "SMART-PARSE", label: "Smart Extract", position: at(2) },
      { id: "store-1", factoryKey: "docxiq-export", label: "Store", position: at(3) },
    ],
    edges: [
      { source: "upload-1", target: "ocr-1" },
      { source: "ocr-1", target: "extract-1" },
      { source: "extract-1", target: "store-1" },
    ],
  },
  {
    key: "invoice-to-excel",
    title: "Invoice → AI Extract → Excel",
    description: "Extract invoice fields with Gemini and export the results to a spreadsheet.",
    category: "AI Extraction",
    icon: FileSpreadsheet,
    nodes: [
      { id: "upload-1", factoryKey: "document-upload", label: "File Upload", position: at(0) },
      { id: "gemini-1", factoryKey: "gemini-understanding", label: "Gemini Extract", position: at(1) },
      { id: "excel-1", factoryKey: "EXCEL", label: "Export to Excel", position: at(2) },
    ],
    edges: [
      { source: "upload-1", target: "gemini-1" },
      { source: "gemini-1", target: "excel-1" },
    ],
  },
  {
    key: "summarize-notify",
    title: "New Document → Summarize → Webhook",
    description: "When a document lands in a store, summarize it and push the summary to an external system.",
    category: "Integration",
    icon: BellRing,
    nodes: [
      { id: "trigger-1", factoryKey: "document-store", label: "Document Store", position: at(0) },
      { id: "summary-1", factoryKey: "summarize", label: "Summarize (LLM)", position: at(1) },
      { id: "webhook-1", factoryKey: "webhook", label: "Webhook", position: at(2) },
    ],
    edges: [
      { source: "trigger-1", target: "summary-1" },
      { source: "summary-1", target: "webhook-1" },
    ],
  },
  {
    key: "gmail-attachments-digest",
    title: "Gmail Attachments → Parse → Send Digest",
    description: "Read inbox attachments, parse them, summarize, and email a digest back.",
    category: "Email",
    icon: Mail,
    nodes: [
      { id: "gmail-1", factoryKey: "GMAIL_CONNECTOR", label: "Gmail Trigger", position: at(0) },
      { id: "emailocr-1", factoryKey: "email-parsing", label: "Email Parse", position: at(1) },
      { id: "summary-1", factoryKey: "summarize", label: "Summarize (LLM)", position: at(2) },
      { id: "send-1", factoryKey: "GMAIL-SEND", label: "Gmail Send", position: at(3) },
    ],
    edges: [
      { source: "gmail-1", target: "emailocr-1" },
      { source: "emailocr-1", target: "summary-1" },
      { source: "summary-1", target: "send-1" },
    ],
  },
  {
    key: "scheduled-scrape-store",
    title: "Scheduled Web Scrape → Store",
    description: "On a schedule, crawl a website and ingest the scraped content into a document store.",
    category: "Automation",
    icon: Globe,
    nodes: [
      { id: "schedule-1", factoryKey: "schedule", label: "Schedule", position: at(0) },
      { id: "scraper-1", factoryKey: "web-scraper", label: "Web Scraper", position: at(1) },
      { id: "store-1", factoryKey: "docxiq-export", label: "Store", position: at(2) },
    ],
    edges: [
      { source: "schedule-1", target: "scraper-1" },
      { source: "scraper-1", target: "store-1" },
    ],
  },

  // ─────────────────────── Real-life use cases ───────────────────────

  {
    key: "resume-screening",
    title: "Resume Screening & Shortlist",
    description:
      "HR drops a batch of CVs in — Gemini extracts skills, years of experience and education, an LLM scores fit against the role, and the recruiter gets an emailed shortlist. Cuts hours of manual screening per opening.",
    category: "HR / Recruiting",
    icon: Users,
    nodes: [
      { id: "upload-1", factoryKey: "document-upload", label: "Upload CVs", position: at(0) },
      { id: "gemini-1", factoryKey: "gemini-understanding", label: "Extract Skills", position: at(1) },
      { id: "score-1", factoryKey: "summarize", label: "Score Fit (LLM)", position: at(2) },
      { id: "send-1", factoryKey: "GMAIL-SEND", label: "Email Shortlist", position: at(3) },
    ],
    edges: [
      { source: "upload-1", target: "gemini-1" },
      { source: "gemini-1", target: "score-1" },
      { source: "score-1", target: "send-1" },
    ],
  },
  {
    key: "contract-review",
    title: "Contract Clause & Risk Review",
    description:
      "Upload a contract and the workflow OCRs it, then an LLM surfaces key clauses, obligations, renewal dates and risk flags into a clean review note saved back to your document store. Speeds up legal first-pass review.",
    category: "Legal",
    icon: FileSignature,
    nodes: [
      { id: "upload-1", factoryKey: "document-upload", label: "Upload Contract", position: at(0) },
      { id: "ocr-1", factoryKey: "ocr-processing", label: "OCR", position: at(1) },
      { id: "review-1", factoryKey: "summarize", label: "Clause & Risk Review", position: at(2) },
      { id: "store-1", factoryKey: "docxiq-export", label: "Save Review", position: at(3) },
    ],
    edges: [
      { source: "upload-1", target: "ocr-1" },
      { source: "ocr-1", target: "review-1" },
      { source: "review-1", target: "store-1" },
    ],
  },
  {
    key: "invoice-to-database",
    title: "Invoice Capture → Database (AP)",
    description:
      "Accounts-payable automation: extract vendor, invoice number, dates, line items and totals from each invoice and write a structured row straight into your PostgreSQL ledger — no manual data entry.",
    category: "Finance / AP",
    icon: Receipt,
    nodes: [
      { id: "upload-1", factoryKey: "document-upload", label: "Upload Invoice", position: at(0) },
      { id: "gemini-1", factoryKey: "gemini-understanding", label: "Extract Fields", position: at(1) },
      { id: "pg-1", factoryKey: "postgres", label: "Insert Row", position: at(2) },
    ],
    edges: [
      { source: "upload-1", target: "gemini-1" },
      { source: "gemini-1", target: "pg-1" },
    ],
  },
  {
    key: "support-inbox-triage",
    title: "Support Inbox Triage",
    description:
      "Reads incoming support emails, parses the body and attachments, then an LLM classifies the issue, sets a priority and drafts a reply — pushing a ready-to-action ticket to your helpdesk via webhook.",
    category: "Customer Support",
    icon: LifeBuoy,
    nodes: [
      { id: "gmail-1", factoryKey: "GMAIL_CONNECTOR", label: "Inbox Trigger", position: at(0) },
      { id: "parse-1", factoryKey: "email-parsing", label: "Parse Email", position: at(1) },
      { id: "triage-1", factoryKey: "summarize", label: "Classify & Draft", position: at(2) },
      { id: "ticket-1", factoryKey: "webhook", label: "Create Ticket", position: at(3) },
    ],
    edges: [
      { source: "gmail-1", target: "parse-1" },
      { source: "parse-1", target: "triage-1" },
      { source: "triage-1", target: "ticket-1" },
    ],
  },
  {
    key: "bank-statement-reconcile",
    title: "Bank Statement → Reconciliation Sheet",
    description:
      "Turns scanned or PDF bank statements into a tidy spreadsheet: OCR the pages, extract each transaction (date, description, debit/credit, balance), and export to Excel ready for reconciliation.",
    category: "Finance",
    icon: Landmark,
    nodes: [
      { id: "upload-1", factoryKey: "document-upload", label: "Upload Statement", position: at(0) },
      { id: "ocr-1", factoryKey: "ocr-processing", label: "OCR", position: at(1) },
      { id: "extract-1", factoryKey: "SMART-PARSE", label: "Extract Transactions", position: at(2) },
      { id: "excel-1", factoryKey: "EXCEL", label: "Export to Excel", position: at(3) },
    ],
    edges: [
      { source: "upload-1", target: "ocr-1" },
      { source: "ocr-1", target: "extract-1" },
      { source: "extract-1", target: "excel-1" },
    ],
  },
  {
    key: "regulatory-change-monitor",
    title: "Regulatory Change Monitor",
    description:
      "Compliance teams stay ahead of rule changes: on a schedule the workflow crawls a regulator's site, an LLM summarizes what changed since last time, and emails the compliance team an alert. No more manual page-watching.",
    category: "Compliance",
    icon: ShieldCheck,
    nodes: [
      { id: "schedule-1", factoryKey: "schedule", label: "Schedule", position: at(0) },
      { id: "scraper-1", factoryKey: "web-scraper", label: "Crawl Site", position: at(1) },
      { id: "summary-1", factoryKey: "summarize", label: "Summarize Changes", position: at(2) },
      { id: "send-1", factoryKey: "GMAIL-SEND", label: "Alert Team", position: at(3) },
    ],
    edges: [
      { source: "schedule-1", target: "scraper-1" },
      { source: "scraper-1", target: "summary-1" },
      { source: "summary-1", target: "send-1" },
    ],
  },
  {
    key: "meeting-notes-actions",
    title: "Meeting Notes → Action Items",
    description:
      "Upload raw meeting notes or a transcript; an LLM pulls out decisions and action items with owners, formats polished minutes as a Word document, and emails them to attendees right after the call.",
    category: "Productivity",
    icon: ListChecks,
    nodes: [
      { id: "upload-1", factoryKey: "document-upload", label: "Upload Notes", position: at(0) },
      { id: "extract-1", factoryKey: "summarize", label: "Extract Actions", position: at(1) },
      { id: "word-1", factoryKey: "WORD", label: "Format Minutes", position: at(2) },
      { id: "send-1", factoryKey: "GMAIL-SEND", label: "Email Attendees", position: at(3) },
    ],
    edges: [
      { source: "upload-1", target: "extract-1" },
      { source: "extract-1", target: "word-1" },
      { source: "word-1", target: "send-1" },
    ],
  },
  {
    key: "purchase-order-intake",
    title: "Purchase Order Intake → ERP",
    description:
      "When a PO lands in the document store, extract its fields (supplier, items, quantities, totals), record it in PostgreSQL, and notify your ERP via webhook — turning emailed PDFs into structured procurement data automatically.",
    category: "Procurement",
    icon: ShoppingCart,
    nodes: [
      { id: "trigger-1", factoryKey: "document-store", label: "PO Received", position: at(0) },
      { id: "extract-1", factoryKey: "SMART-PARSE", label: "Extract PO Fields", position: at(1) },
      { id: "pg-1", factoryKey: "postgres", label: "Record PO", position: at(2) },
      { id: "erp-1", factoryKey: "webhook", label: "Notify ERP", position: at(3) },
    ],
    edges: [
      { source: "trigger-1", target: "extract-1" },
      { source: "extract-1", target: "pg-1" },
      { source: "pg-1", target: "erp-1" },
    ],
  },
  {
    key: "kyc-id-extraction",
    title: "ID / KYC Document Extraction",
    description:
      "Onboarding automation for banking and fintech: high-accuracy Cloud OCR reads scanned IDs, Gemini extracts name, date of birth and document number, and the verified record is saved for KYC review.",
    category: "Onboarding / KYC",
    icon: ScanLine,
    nodes: [
      { id: "upload-1", factoryKey: "document-upload", label: "Upload ID", position: at(0) },
      { id: "vision-1", factoryKey: "cloud-vision", label: "Cloud OCR", position: at(1) },
      { id: "extract-1", factoryKey: "gemini-understanding", label: "Extract ID Fields", position: at(2) },
      { id: "store-1", factoryKey: "docxiq-export", label: "Save Record", position: at(3) },
    ],
    edges: [
      { source: "upload-1", target: "vision-1" },
      { source: "vision-1", target: "extract-1" },
      { source: "extract-1", target: "store-1" },
    ],
  },
  {
    key: "daily-metrics-report",
    title: "Daily Metrics Report",
    description:
      "Every morning the workflow pulls KPIs from your API, transforms them into a clean layout, renders a branded PDF, and emails it to stakeholders — a hands-off daily report that runs while you sleep.",
    category: "Reporting",
    icon: BarChart3,
    nodes: [
      { id: "schedule-1", factoryKey: "schedule", label: "Daily Schedule", position: at(0) },
      { id: "fetch-1", factoryKey: "http-api", label: "Fetch KPIs", position: at(1) },
      { id: "transform-1", factoryKey: "transform", label: "Shape Data", position: at(2) },
      { id: "pdf-1", factoryKey: "pdf-generator", label: "Render PDF", position: at(3) },
      { id: "send-1", factoryKey: "GMAIL-SEND", label: "Email Report", position: at(4) },
    ],
    edges: [
      { source: "schedule-1", target: "fetch-1" },
      { source: "fetch-1", target: "transform-1" },
      { source: "transform-1", target: "pdf-1" },
      { source: "pdf-1", target: "send-1" },
    ],
  },
];

/** localStorage key the editor reads to hydrate a chosen template into a fresh workflow. */
export const PENDING_TEMPLATE_KEY = "pending_workflow_template";
